import {
  TIER1_PRIMARY,
  TRANSLATION_PRIMARY,
  DOCUMENT_ANALYSIS_PRIMARY,
  FORMAT_REPORT_PRIMARY,
  DEEP_RESEARCH_PRIMARY,
  DEEP_RESEARCH_QUALIFICATION_PRIMARY,
  DEEP_RESEARCH_LADDER,
  SSOT_MODEL_EFFORT,
} from './modelTiers.generated';

/**
 * The Perplexity proxy speaks bare Perplexity model ids (e.g. 'sonar-deep-research'),
 * while the SSOT-generated constants carry the OpenRouter form ('perplexity/...').
 * Strip the provider segment at the consumption boundary so the pins stay
 * registry-resolved end to end (2026-08-15 citation-cutoff fix).
 */
const barePerplexityId = (id: string): string => id.replace(/^perplexity\//, '');
// 2026-05-10: REL-006 Sonnet downgrade reversed per Miles directive.
// Opus-4-7 primary across all Claude paths. Performance-tier fallbacks only
// (GPT-5.5 → Gemini-3.1-Pro). NO Sonnet / Haiku in any Claude primary or
// fallback ladder. See Slack C0AM8U804KZ/1778388785.311969.
/**
 * Agentic Workflow Configuration
 *
 * Central configuration for the deep research agentic workflow.
 * Controls model selection for testing vs production modes.
 */

// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================

/**
 * Detects whether the code is running in a local development environment.
 *
 * Browser context: checks window.location.hostname for localhost/127.0.0.1
 * Node/test context: checks NODE_ENV !== 'production'
 *
 * Used to auto-enable TESTING_ENVIRONMENT on localhost so wallet records,
 * chat messages, and transactions are tagged as "test example" and users
 * are never charged real money during local development.
 */
function isLocalEnvironment(): boolean {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV !== 'production';
  }
  return false;
}

// ============================================================================
// TESTING MODE TOGGLE
// ============================================================================

/**
 * Testing Mode Toggle
 *
 * When TRUE (testing mode):
 * - Perplexity: Uses sonar-reasoning-pro (faster, cheaper)
 * - Claude: Uses claude-opus-4-7 (Opus everywhere per Miles 2026-05-10 directive)
 * - Extended Thinking: DISABLED (faster, lower cost)
 * - Perplexity Reasoning: 'medium' effort
 *
 * When FALSE (production mode):
 * - Perplexity: Uses sonar-deep-research (comprehensive)
 * - Claude: Uses claude-opus-4-7 (highest quality, adaptive thinking)
 * - Extended Thinking: ENABLED with 16,000 budget tokens (maximum reasoning)
 * - Perplexity Reasoning: 'high' effort
 *
 * IMPORTANT: Set to FALSE before deploying to production!
 */
export const TESTING_MODE = false; // <-- SINGLE TOGGLE: Change this to switch modes

/**
 * Testing Environment Toggle
 *
 * When TRUE: Wallet records (expenses, earnings, usage) are tagged as "test example"
 * This is separate from TESTING_MODE which controls model selection.
 *
 * Use this when you want production-quality models but still want to mark
 * wallet records as test data.
 *
 * IMPORTANT: Set to FALSE before deploying to production!
 */
export const TESTING_ENVIRONMENT = false; // Auto-detected: true on localhost, false in production

/**
 * Combined flag: TRUE when either TESTING_MODE or TESTING_ENVIRONMENT is enabled.
 * Used by wallet repositories to determine if records should be tagged as "test example".
 */
export const SHOULD_TAG_AS_TEST = TESTING_MODE || TESTING_ENVIRONMENT;

// ============================================================================
// Runtime Testing Environment (Admin Toggle + Auto-detect Dev/Preview)
// ============================================================================

/**
 * Returns true if the current window origin is a local dev or Firebase preview environment.
 * - localhost / 127.0.0.1 (any port)
 * - *.web.app (Firebase Hosting preview channels)
 * - *.firebaseapp.com (Firebase Hosting)
 */
export function isDevPreviewOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.web.app') ||
    hostname.endsWith('.firebaseapp.com')
  );
}

/**
 * localStorage key for the admin testing toggle. Mirrors
 * STORAGE_KEYS.TESTING_ENVIRONMENT in src/constants/index.ts (inlined to keep
 * this a dependency-free leaf config module). Persisting here is what makes the
 * toggle survive a page reload and NOT silently revert to real billing.
 */
const RUNTIME_TESTING_STORAGE_KEY = 'testing_environment_enabled';

/**
 * Read the persisted admin testing toggle from localStorage (SSR/where-window-
 * is-undefined safe). Called once at module load so the flag is authoritative
 * from the first render, before Settings is ever opened.
 */
function readPersistedRuntimeTesting(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(RUNTIME_TESTING_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mutable runtime flag set by the admin toggle in Settings. Hydrated
 * synchronously at module load from localStorage so a reload/new tab keeps the
 * admin's testing choice instead of defaulting to real billing.
 *
 * IMPORTANT: this flag is the CLIENT-side display/tagging hint only. The
 * authoritative billing-pause decision is verified SERVER-side (the metering
 * Cloud Functions re-read the account owner's `testing_environment` from
 * Firestore) — a self-writable client flag must never be trusted to suppress a
 * real wallet debit on its own.
 */
let _runtimeTestingEnvironment = readPersistedRuntimeTesting();

/**
 * Set the runtime testing environment flag (called by the admin toggle in
 * Settings). Persists to localStorage so the choice survives reloads and tab
 * switches. Pass `{ persist: false }` for ephemeral/derived updates that must
 * NOT overwrite the admin's stored preference (e.g. dev-origin auto-detect).
 */
export function setRuntimeTestingEnvironment(
  enabled: boolean,
  opts?: { persist?: boolean },
): void {
  _runtimeTestingEnvironment = enabled;
  if (opts?.persist === false) return;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (enabled) {
        window.localStorage.setItem(RUNTIME_TESTING_STORAGE_KEY, 'true');
      } else {
        window.localStorage.removeItem(RUNTIME_TESTING_STORAGE_KEY);
      }
    }
  } catch {
    /* localStorage unavailable (private mode / SSR) — in-memory flag still set */
  }
}

/**
 * Re-hydrate the runtime flag from localStorage. Safe to call on focus /
 * visibilitychange / app boot: it reads the PERSISTED admin choice and never
 * clobbers it with a derived value. Returns the resolved shouldTagAsTest().
 */
export function refreshRuntimeTestingFromStorage(): boolean {
  _runtimeTestingEnvironment = readPersistedRuntimeTesting();
  return shouldTagAsTest();
}

/**
 * Returns true when any testing/tagging condition is active:
 * - Compile-time TESTING_MODE flag
 * - Compile-time TESTING_ENVIRONMENT flag
 * - Admin-toggled runtime flag (_runtimeTestingEnvironment)
 * - Running on a dev / Firebase preview origin
 *
 * OpenRouter 3-tier key routing (based on this return value):
 *   - true  → OPENROUTER_API_KEY_TEST ("Prest0 testing" key) — testing/dev/preview
 *   - false → OPENROUTER_API_KEY_USER ("Prest0 user usage" key) — production user workflows
 *   (A separate "Prest0n" key handles internal Slack/agent usage and is NOT in this codebase)
 */
export function shouldTagAsTest(): boolean {
  return TESTING_MODE || TESTING_ENVIRONMENT || _runtimeTestingEnvironment || isDevPreviewOrigin();
}

/**
 * Returns true when the admin has explicitly turned on the testing toggle at runtime.
 */
export function isAdminTestingToggleOn(): boolean {
  return _runtimeTestingEnvironment;
}

// ============================================================================
// USER COST MULTIPLIER
// ============================================================================

/**
 * User-Facing Cost Multiplier
 *
 * All AI token costs are multiplied by this value before being charged to users.
 * Accounts for operational costs, support, infrastructure, and service markup.
 *
 * Example: At 5.0X, a $0.01 API cost becomes $0.05 for the user.
 *
 * THIS IS THE CANONICAL SOURCE for the cost multiplier. It is used in:
 *   1. Frontend: src/config/aiModelPricing.ts  (imported directly from here)
 *   2. Backend:  functions/src/index.ts         (manually synced — see comment there)
 *
 * If you change this value, you MUST also update the backend copy in:
 *   functions/src/index.ts  (search for WIDGET_USER_COST_MULTIPLIER)
 * Then rebuild the backend:  cd functions && npm run build
 */
export const USER_COST_MULTIPLIER = 5.0;

// ============================================================================
// Model Configuration Based on Testing Mode
// ============================================================================

/**
 * Perplexity model for Stage 3 deep research
 */
// 2026-08-15 citation-cutoff fix: registry-resolved from the SSOT (deep-research ->
// tier-14 primary; testing rides the tier-13 qualification primary). Never hand-pin.
export const PERPLEXITY_DEEP_RESEARCH_MODEL = TESTING_MODE
  ? barePerplexityId(DEEP_RESEARCH_QUALIFICATION_PRIMARY)
  : barePerplexityId(DEEP_RESEARCH_PRIMARY);

/**
 * Tier-14 Fallback 1 (bare Perplexity id) — the rung the perplexityClient descends to
 * when the deep-research primary fails, and the model for the PRE-215 citations-recovery
 * call in deepResearch.ts. Registry-resolved (2026-08-15 citation-cutoff fix).
 */
export const PERPLEXITY_DEEP_RESEARCH_FALLBACK_MODEL = barePerplexityId(DEEP_RESEARCH_LADDER[1]);

/**
 * Display name for Perplexity model (for transaction records)
 * Uses code name "Echo" instead of "Perplexity"
 */
export const PERPLEXITY_DEEP_RESEARCH_DISPLAY = TESTING_MODE
  ? 'Echo Reasoning Pro'
  : 'Echo Deep Research';

/**
 * Claude model for document analysis
 */
// 2026-05-10 (Miles directive, reverses REL-006): Opus-4-7 across both branches.
// In-Claude fallback to Sonnet/Haiku is forbidden; cross-provider performance
// ladder (GPT-5.5 → Gemini-3.1-Pro) handles outage cases via OpenRouter.
// 2026-08-22 MISROUTE FIX (same class as the 2026-08-15 format-report fix):
// this constant consumed TIER1_PRIMARY, whose meaning silently changed to
// "default spawn primary" (x-ai/grok-4.6 today) — so client document analysis,
// vision transcription and scanned-page OCR rode the tier-4 orchestrator model
// while the SSOT pins document-analysis -> tier-11-document. Consume the
// route-resolved constant; a within-tier swap now reaches here by regeneration.
export const CLAUDE_DOCUMENT_ANALYSIS_MODEL = TESTING_MODE
  ? DOCUMENT_ANALYSIS_PRIMARY
  : DOCUMENT_ANALYSIS_PRIMARY;

/**
 * Display name for Claude document analysis (for transaction records)
 * Uses code name Apex-4.8 (Opus) on both branches per 2026-05-10 directive.
 */
export const CLAUDE_DOCUMENT_ANALYSIS_DISPLAY = TESTING_MODE
  ? 'Apex-5 (Document Analysis)'
  : 'Apex-5 (Document Analysis)';

/**
 * Fallback model for document analysis (intra-Claude)
 * Per 2026-05-10 Miles directive: NO Sonnet/Haiku fallback. Stay on Opus and
 * let the cross-provider OPENROUTER_FALLBACK_* ladder handle Claude outages.
 */
export const CLAUDE_DOCUMENT_ANALYSIS_FALLBACK = TESTING_MODE
  ? DOCUMENT_ANALYSIS_PRIMARY
  : DOCUMENT_ANALYSIS_PRIMARY;

// ============================================================================
// Report Formatter Configuration
// ============================================================================

/**
 * Claude model for report formatting (online audience)
 *
 * REL-006-1 (2026-04-25): Downgraded production from claude-opus-4-7 to
 * claude-sonnet-4-6.
 * 2026-05-10 (Miles directive): REL-006-1 REVERSED. Back to Opus-4-7 across
 * both branches. Cost increase intentional. NO Sonnet/Haiku in this path.
 * 2026-08-15 MISROUTE FIX: this constant consumed TIER1_PRIMARY, whose meaning
 * silently changed to "default spawn primary" (x-ai/grok-4.6 today) when the
 * subagent-default route moved off tier 1 — so the format-report stage of deep
 * research rode Grok while displaying "Apex". SSOT taskRouting pins
 * format-report -> tier-1-performance; consume the route-resolved constant.
 */
export const CLAUDE_FORMATTER_MODEL = TESTING_MODE
  ? FORMAT_REPORT_PRIMARY
  : FORMAT_REPORT_PRIMARY;

/**
 * Display name for Claude formatter (for transaction records). Only the
 * fallback: buildModelDisplay names the SERVED model whenever the usage
 * reports one. Follows the format-report primary (tier 1: Opus 5.5 since the
 * 2026-09-27 re-ladder) on both branches.
 */
export const CLAUDE_FORMATTER_DISPLAY = TESTING_MODE
  ? 'Apex-5.5 (Formatting)'
  : 'Apex-5.5 (Formatting)';

/**
 * Fallback model for formatter (intra-Claude)
 * Per 2026-05-10 Miles directive: NO Sonnet/Haiku fallback. Stay on Opus.
 * 2026-08-15: route-resolved (see CLAUDE_FORMATTER_MODEL note).
 */
export const CLAUDE_FORMATTER_FALLBACK = TESTING_MODE
  ? FORMAT_REPORT_PRIMARY
  : FORMAT_REPORT_PRIMARY;

// ============================================================================
// Spanish Translator Configuration
// ============================================================================

/**
 * Claude model for Spanish translation
 *
 * REL-006-5 (2026-04-25): Downgraded production from claude-opus-4-7 to
 * claude-sonnet-4-6.
 * 2026-05-10 (Miles directive): REL-006-5 REVERSED. Back to Opus-4-7 across
 * both branches. NO Sonnet/Haiku anywhere in this path. Cross-provider ladder
 * (GPT-5.5 → Gemini-3.1-Pro) handles Claude outages.
 */
// 2026-08-22 MISROUTE FIX (see CLAUDE_DOCUMENT_ANALYSIS_MODEL note): rode the
// default-spawn primary (grok-4.6) while SSOT pins translation -> tier-11.
export const CLAUDE_TRANSLATOR_MODEL = TESTING_MODE
  ? TRANSLATION_PRIMARY
  : TRANSLATION_PRIMARY;

/**
 * Display name for Claude translator (for transaction records)
 * Uses code name Apex-4.8 (Opus) on both branches per 2026-05-10 directive.
 */
export const CLAUDE_TRANSLATOR_DISPLAY = TESTING_MODE
  ? 'Apex-5 (Translation)'
  : 'Apex-5 (Translation)';

/**
 * Fallback model for translator (intra-Claude)
 * Per 2026-05-10 Miles directive: NO Sonnet/Haiku fallback. Stay on Opus.
 */
export const CLAUDE_TRANSLATOR_FALLBACK = TESTING_MODE
  ? TRANSLATION_PRIMARY
  : TRANSLATION_PRIMARY;

// ============================================================================
// Video Script Configuration
// ============================================================================

/**
 * Claude model for video script Stage 1 (analysis/clarification)
 * 2026-05-10 (Miles directive): flipped Sonnet → Opus-4-7. Higher latency but
 * Opus is mandated across all Claude paths. Cross-provider performance ladder
 * (GPT-5.5 → Gemini-3.1-Pro) handles outages.
 */
export const CLAUDE_VIDEO_SCRIPT_STAGE1_MODEL = TIER1_PRIMARY;

/**
 * Claude model for video script Stage 2 (script generation)
 * 2026-05-10 (Miles directive): Opus-4-7 across both branches (was Sonnet in test).
 */
export const CLAUDE_VIDEO_SCRIPT_STAGE2_MODEL = TESTING_MODE
  ? TIER1_PRIMARY
  : TIER1_PRIMARY;

/**
 * Legacy alias - points to Stage 2 model for backward compatibility
 * @deprecated Use CLAUDE_VIDEO_SCRIPT_STAGE1_MODEL or CLAUDE_VIDEO_SCRIPT_STAGE2_MODEL instead
 */
export const CLAUDE_VIDEO_SCRIPT_MODEL = CLAUDE_VIDEO_SCRIPT_STAGE2_MODEL;

/**
 * Display name for Claude video script Stage 1 analysis (for transaction records)
 * 2026-05-10: Stage 1 flipped to Opus-4-7. Display shows Apex-4.8.
 */
export const CLAUDE_VIDEO_SCRIPT_ANALYSIS_DISPLAY = 'Apex-4.8 (Script Analysis)';

/**
 * Display name for Claude video script Stage 2 generation (for transaction records)
 * Uses code names: Nova (Sonnet), Apex-4.8 (Opus)
 *
 * Stale-label fix (2026-04-25): production swap from Apex-4.6 -> Apex-4.8 to
 * match CLAUDE_VIDEO_SCRIPT_STAGE2_MODEL, which has been on claude-opus-4-7
 * for some time. The display string was out of sync with the real model.
 */
export const CLAUDE_VIDEO_SCRIPT_GENERATION_DISPLAY = TESTING_MODE
  ? 'Apex-4.8 (Script Generation)'
  : 'Apex-4.8 (Script Generation)';

/**
 * Fallback model for video script (both stages, intra-Claude)
 * Per 2026-05-10 Miles directive: NO Sonnet/Haiku fallback. Stay on Opus.
 */
export const CLAUDE_VIDEO_SCRIPT_FALLBACK = TESTING_MODE
  ? TIER1_PRIMARY
  : TIER1_PRIMARY;

// ============================================================================
// Deep Research Workflow Stage-Specific Configuration
// ============================================================================

/**
 * Perplexity model for Deep Research Stage 1 (qualification/assessment)
 * ALWAYS uses sonar-reasoning-pro for fast, reasoning-focused assessment
 * Does NOT change based on TESTING_MODE
 */
export const PERPLEXITY_DEEP_RESEARCH_STAGE1_MODEL = barePerplexityId(DEEP_RESEARCH_QUALIFICATION_PRIMARY);

/**
 * Display name for Deep Research Stage 1 (for transaction records)
 */
export const PERPLEXITY_DEEP_RESEARCH_STAGE1_DISPLAY = 'Echo Reasoning Pro';

/**
 * Perplexity model for Deep Research Stage 2 (strategy/brief)
 * ALWAYS uses the tier-13 web-search primary for fast, reasoning-focused planning
 * Does NOT change based on TESTING_MODE
 *
 * DELIBERATE POSTURE (2026-08-15): the client Stage 2 grounded brief rides the
 * tier-13 qualification rung, NOT the SSOT 'deep-research-blueprint' -> tier-1
 * (Opus) route. The tier-1 blueprint label covers the server-side webchat
 * blueprint TURN (a client-facing prose turn); the desktop Stage 2 exists to
 * produce grounded citations[] that Stage 3 anchors bracket cites against
 * (AUTHORITATIVE_SOURCES, MT-QA-BUG-008) — an Opus rung on this path has no web
 * grounding and would silently kill that citation-resilience mechanism.
 */
export const PERPLEXITY_DEEP_RESEARCH_STAGE2_MODEL = barePerplexityId(DEEP_RESEARCH_QUALIFICATION_PRIMARY);

/**
 * Display name for Deep Research Stage 2 (for transaction records)
 * Uses same display name as Stage 1 since both use the same model
 * The transaction 'summary' field distinguishes them ('topic assessment' vs 'built research strategy')
 */
export const PERPLEXITY_DEEP_RESEARCH_STAGE2_DISPLAY = 'Echo Reasoning Pro';

/**
 * Perplexity reasoning effort for Deep Research Stages 1 & 2
 * ALWAYS 'high' for maximum quality reasoning - does NOT depend on TESTING_MODE
 */
export const PERPLEXITY_DEEP_RESEARCH_STAGES_1_2_REASONING_EFFORT: 'low' | 'medium' | 'high' = 'high';

// ============================================================================
// Video Script Stage 1 Configuration (Perplexity)
// ============================================================================

/**
 * Perplexity model for video script Stage 1 (analysis/clarification)
 * ALWAYS uses sonar-reasoning-pro with high reasoning effort
 * Does NOT change based on TESTING_MODE
 */
export const PERPLEXITY_VIDEO_SCRIPT_STAGE1_MODEL = 'sonar-reasoning-pro';

/**
 * Display name for Perplexity video script Stage 1 (for transaction records)
 * Uses code name "Echo" instead of "Perplexity"
 */
export const PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY = 'Echo Reasoning (Script Analysis)';

/**
 * Perplexity reasoning effort for video script Stage 1
 * ALWAYS 'high' for maximum quality analysis - does NOT depend on TESTING_MODE
 */
export const PERPLEXITY_VIDEO_SCRIPT_STAGE1_REASONING_EFFORT: 'low' | 'medium' | 'high' = 'high';

// ============================================================================
// Extended Thinking / Reasoning Configuration
// ============================================================================

/**
 * Whether to enable extended thinking (reasoning) for Claude models
 * Production mode enables highest quality reasoning for best results
 * Testing mode disables it for faster responses and lower costs
 */
export const ENABLE_EXTENDED_THINKING = !TESTING_MODE;

/**
 * Budget tokens for extended thinking (reasoning depth)
 * Higher = more thorough reasoning, more tokens used
 * Production: 16,000 tokens (maximum reasoning depth)
 * Testing: 0 tokens (disabled to save costs)
 */
export const THINKING_BUDGET_TOKENS = TESTING_MODE ? 0 : 16000;

/**
 * Extended thinking configuration object for Claude API requests
 * Only included when ENABLE_EXTENDED_THINKING is true
 */
export const getThinkingConfig = () => ENABLE_EXTENDED_THINKING
  ? { type: 'enabled' as const, budget_tokens: THINKING_BUDGET_TOKENS }
  : undefined;

/**
 * Perplexity reasoning effort level
 * Always 'high' for maximum reasoning quality across all stages and modes.
 * Per Perplexity API docs, omitting or lowering this causes weaker research output.
 */
export const PERPLEXITY_REASONING_EFFORT: 'low' | 'medium' | 'high' = 'high';

/**
 * Maximum tokens for Stage 3 deep research responses.
 *
 * Production (sonar-deep-research): 64,000 tokens
 *   - Model supports up to 128,000 output tokens
 *   - At 32,000 the model frequently hit the token ceiling with the analytical brief format,
 *     causing mid-paragraph truncation (finish_reason: "length")
 *   - At 64,000 the model has sufficient headroom for 20,000-30,000+ word analytical briefs
 *   - Non-streaming mode returns <think> blocks separately (not in completion tokens)
 *
 * Testing (sonar-reasoning-pro): 16,000 tokens
 *   - Standard Sonar models have a practical output ceiling of ~16,000 tokens
 *   - Sending 32,000 to sonar-reasoning-pro causes Perplexity API 500 errors
 *   - 16,000 is sufficient for comprehensive testing reports
 *
 * Reasoning tokens and search queries are billed separately.
 */
export const PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS = TESTING_MODE ? 16000 : 64000;

/**
 * Maximum tokens for translation responses.
 *
 * Must accommodate BOTH extended thinking budget AND the full translated output.
 * Deep research reports can be up to 32,000 tokens, and translations to languages
 * like Spanish are typically 15-25% longer. So output capacity needs ~40,000 tokens.
 *
 * With extended thinking (production): 16,000 thinking + 48,000 output = 64,000
 * Without extended thinking (testing): 32,000 output (no thinking overhead)
 */
export const TRANSLATION_MAX_TOKENS = ENABLE_EXTENDED_THINKING ? 64000 : 32000;

/**
 * Temperature for Perplexity deep research requests.
 *
 * NOTE: sonar-deep-research does NOT support the temperature parameter.
 * Per the official Perplexity API docs (Feb 2026), sending temperature
 * (even temperature: 0) is treated as an unrecognized parameter and
 * triggers a silent downgrade to medium reasoning_effort.
 *
 * This constant is kept for non-deep-research models (e.g. sonar-reasoning-pro
 * used in Stages 1 & 2). The perplexityClient conditionally omits temperature
 * when the model is sonar-deep-research.
 */
export const PERPLEXITY_DEEP_RESEARCH_TEMPERATURE = 0.2;

/**
 * Search context size for Perplexity deep research requests.
 * Controls how much web evidence Perplexity retrieves during the research phase.
 * Fixed at 'high' for maximum research depth and comprehensiveness.
 *
 * IMPORTANT (Feb 6 2026 Perplexity API change):
 * After this date, sending unrecognized/undocumented parameters causes the API
 * to silently default reasoning_effort to 'medium'. Only send documented params:
 * model, messages, stream, max_tokens, temperature, top_p, top_k,
 * frequency_penalty, presence_penalty, reasoning_effort, web_search_options.
 * Do NOT send undocumented params like stream_mode.
 */
export const PERPLEXITY_SEARCH_CONTEXT_SIZE: 'low' | 'medium' | 'high' = 'high';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get current mode description for logging
 */
export function getModeDescription(): string {
  return TESTING_MODE
    ? `TESTING MODE (cost-saving models, reasoning: OFF)`
    : `PRODUCTION MODE (full-quality models, extended thinking: ${THINKING_BUDGET_TOKENS} tokens, Perplexity reasoning: ${PERPLEXITY_REASONING_EFFORT})`;
}

/**
 * Log current configuration (for debugging)
 */
export function logCurrentConfig(): void {
  console.log(`[AgenticWorkflow] ${getModeDescription()}`);
  console.log(`[AgenticWorkflow] Perplexity Stage 3: ${PERPLEXITY_DEEP_RESEARCH_MODEL}`);
  console.log(`[AgenticWorkflow] Claude Document Analysis: ${CLAUDE_DOCUMENT_ANALYSIS_MODEL}`);
  console.log(`[AgenticWorkflow] Claude Formatter: ${CLAUDE_FORMATTER_MODEL}`);
  console.log(`[AgenticWorkflow] Claude Translator: ${CLAUDE_TRANSLATOR_MODEL}`);
  console.log(`[AgenticWorkflow] Claude Video Script: ${CLAUDE_VIDEO_SCRIPT_MODEL}`);
  console.log(`[AgenticWorkflow] Claude Orchestrator: ${CLAUDE_ORCHESTRATOR_MODEL}`);
}

// ============================================================================
// Orchestrator Configuration
// ============================================================================

/**
 * Model for the orchestrator (workflow routing classifier).
 *
 * 2026-08-15 CLARIFICATION: this rides TIER1_PRIMARY — the DEFAULT SPAWN
 * primary (x-ai/grok-4.6 today), NOT an Opus rung. That is SSOT-conformant:
 * the client workflow classifier is a general-default-plane call (same class
 * as planner-classifier/fast-clarify -> tier-4-general-default). The stale
 * "Opus-4-7 across both branches" comment and the Apex display string here
 * were lies — transaction records claimed Claude while the wire carried Grok
 * (see ANALYSIS-model-routing-20260814.md, "misleading 'Apex-4.8' displays").
 * The MODEL is intentionally unchanged; the display now tells the truth.
 */
export const CLAUDE_ORCHESTRATOR_MODEL = TESTING_MODE
  ? TIER1_PRIMARY
  : TIER1_PRIMARY;

/**
 * Display name for the orchestrator (for transaction records).
 * 2026-08-15: was 'Apex-4.8 (Orchestrator)' while the wire carried grok-4.6 —
 * records misattributed the spend to Claude. Pulse is the x-ai/Grok codename;
 * keep in lockstep with TIER1_PRIMARY / providerCodeNames.ts.
 */
export const CLAUDE_ORCHESTRATOR_DISPLAY = TESTING_MODE
  ? 'Pulse-4.6 (Orchestrator)'
  : 'Pulse-4.6 (Orchestrator)';

/**
 * Orchestrator reasoning effort — SSOT-RESOLVED (2026-08-20 decision doctrine).
 *
 * HISTORY: this used to be two constants that were both wrong on the wire. The old
 * `ORCHESTRATOR_THINKING_CONFIG {type:'enabled', budget_tokens:4000}` is an
 * ANTHROPIC-only parameter shape that was sent verbatim to the grok-4.6 primary
 * (which ignores it — the central webchat routing decision ran at the provider
 * default), and `ORCHESTRATOR_REASONING_LEVEL: 'high'` was stamped into ⓘ telemetry
 * without ever reaching the wire — the display asserted an effort that was never
 * sent. The orchestrator now sends `reasoning: { effort }` resolved from the SSOT's
 * configured thinking for its serving model, and the telemetry stamp is the SAME
 * value — wire and display cannot diverge again.
 */
export const ORCHESTRATOR_SSOT_EFFORT: string =
  SSOT_MODEL_EFFORT[
    CLAUDE_ORCHESTRATOR_MODEL.toLowerCase().replace(/^openrouter\//, '').replace(/^~/, '')
  ] ?? 'high';

// ============================================================================
// Context Relevance Configuration
// ============================================================================

/**
 * Time gap thresholds for context relevance evaluation.
 * These control when conversation history is considered "stale".
 *
 * Used by the orchestrator, deep research, and video script workflows
 * to determine if previous conversation context should be used.
 */
export const CONTEXT_RELEVANCE_THRESHOLDS = {
  /** Gap considered "recent" - full relevance (5 minutes) */
  RECENT_MS: 5 * 60 * 1000,
  /** Gap considered "moderate" - partial relevance (30 minutes) */
  MODERATE_MS: 30 * 60 * 1000,
  /** Gap considered "stale" - low relevance (2 hours) */
  STALE_MS: 2 * 60 * 60 * 1000,
  /** Gap considered "expired" - ignore (24 hours) */
  EXPIRED_MS: 24 * 60 * 60 * 1000,
};

/**
 * Whether to enable time-based context filtering.
 * When true, workflows will evaluate time gaps between messages
 * and reduce reliance on old context when gaps are significant.
 */
export const ENABLE_TIME_BASED_CONTEXT = true;

/**
 * Whether to enable the "clarify once" mechanism.
 * When true, the orchestrator will ask at most ONE clarifying question
 * about context relevance when there's uncertainty, then proceed
 * with its best interpretation for subsequent messages.
 */
export const ENABLE_CLARIFY_ONCE = true;

// ============================================================================
// Translation Timeout Configuration
// ============================================================================

/**
 * Total request timeout for translation operations.
 * Production: 15 minutes (extended thinking + large documents)
 * Testing: 10 minutes (faster models, still allow for large docs)
 *
 * The 5-minute default was insufficient because:
 * - Extended thinking uses 16,000 budget tokens in production
 * - Large legal documents take longer to translate
 * - Tab dormancy can throttle JavaScript timers
 * - Claude API may have queue delays during high load
 */
export const TRANSLATION_TOTAL_TIMEOUT_MS = TESTING_MODE
  ? 10 * 60 * 1000  // 10 minutes for testing
  : 15 * 60 * 1000; // 15 minutes for production

/**
 * Per-chunk timeout for streaming responses.
 * Prevents indefinite hangs on individual stream reads.
 * Same for both modes - network issues are mode-independent.
 * Increased from 30s to 60s for reliability.
 */
export const TRANSLATION_CHUNK_TIMEOUT_MS = 60000; // 60 seconds per chunk

/**
 * Human-readable timeout for error messages
 */
export const TRANSLATION_TIMEOUT_DESCRIPTION = TESTING_MODE
  ? '10 minutes'
  : '15 minutes';

// ============================================================================
// OpenRouter Cross-Provider Fallback Configuration
// ============================================================================

/**
 * Cross-provider fallback strategy (via OpenRouter proxy):
 *
 * WHEN CLAUDE IS DOWN (formatter, translator, doc analyzer):
 *   Opus-4-7 (retry 3x) → GPT-5.5 highest thinking (retry 3x) → Gemini-3.1-Pro highest thinking (retry 3x) → error
 *   NO Sonnet/Haiku cascade — go straight to cross-provider.
 *   Order revised 2026-05-10 per Miles directive: GPT-5.5 first, then Gemini.
 *   See Slack C0AM8U804KZ/1778388785.311969.
 *
 * WHEN PERPLEXITY IS DOWN (deep research):
 *   sonar-deep-research (retry 3x) → sonar-pro (retry 3x) → Claude Opus with search (retry 3x)
 *   → Gemini 3.1 Pro with search (retry 3x) → error
 *
 * Codename: "Prism" (never mention "OpenRouter" in user-facing messages).
 */

// D14 slice 7 (2026-09-15): the client-side cross-provider fallback chains
// (Claude-down and Perplexity-down) retired with the deploy-skew fallbacks —
// the task doors descend each route's SSOT ladder server-side, so the
// browser no longer carries any fallback model list.

// End of configuration
